import { useState } from 'react';
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from 'uuid';
import { sendMessageToPract, updateMessageStatus } from "store/actions/clientMessaging";
import { useWebSocket } from "Providers/WebSocketContext";
import LoadingIndicator from "components/shared/LoadingIndicator";
import ChatInput from "./Components/ChatInput";
import MessageList from "./Components/MessageList";
import { CONFIRM_POPUP, ERROR_MESSAGE } from "common/constantMessage";
import { useConversation } from "hooks/useConversation";
import styles from "./MessagingInterface.module.css";
import { deleteChatMessage, editChatMessage } from "store/services/clientMessagingService";
import { isEmpty } from "lodash";

const MessagingInterface = ({ userId, practName, practId, conversationId }) => {
  const dispatch = useDispatch();
  const { sendMessage } = useWebSocket();

  const {
    conversation,
    isLoading,
    messageListRef,
    messagesEndRef,
    showLoadMore,
    handleScroll,
    handleLoadPrevious,
  } = useConversation(userId);

  const [editMessage, setEditMessage] = useState(null);

  const handleSend = (messageText) => {
    if (isEmpty(messageText)) return;

    if (editMessage) {
      const payload = {
        messageId: editMessage.id,
        conversationId,
        values: {
          message: messageText,
        },
      };
      dispatch(editChatMessage(payload, "editing"));
      handleCloseEdit();
      return;
    }

    const msgPayload = {
      action: "sendMessage",
      sender_id: Number(userId),
      receiver_id: Number(practId),
      message: messageText,
      conversation_id: conversationId,
      idempotencyId: uuidv4(),
    };

    dispatch(sendMessageToPract(msgPayload));

    sendMessage(
      msgPayload,
      (ack) => dispatch(updateMessageStatus(ack, "sent")),
      (error) => {
        console.error("Failed to send message:", error);
        dispatch(updateMessageStatus(msgPayload, "failed"));
        ERROR_MESSAGE("Failed to send message. Please try again.");
      }
    );

  };

  const handleDeleteMessage = async (msgId) => {
    try {
      const isConfirm = await CONFIRM_POPUP();
      if (!isConfirm) return;

      const payload = {
        messageId: msgId,
        conversationId,
      }

      await dispatch(deleteChatMessage(payload));
    } catch (error) {
      console.error("Error deleting message:", error);
    }
  }

  const handleEditMessage = async (msg) => setEditMessage(msg);

  const handleCloseEdit = () => setEditMessage(null);

  return (
    <div className={styles.MainChatInterface}>
      {isLoading && <LoadingIndicator centered={true} size={10} className="my-2" />}

      <div className="messages-list">
        {conversation?.messages?.length > 0 ? (
          <MessageList
            userId={userId}
            conversation={conversation}
            practName={practName}
            practId={practId}
            messageListRef={messageListRef}
            messagesEndRef={messagesEndRef}
            onScroll={handleScroll}
            onLoadPrevious={handleLoadPrevious}
            showLoadMore={showLoadMore}
            isLoading={isLoading}
            onEditMessage={handleEditMessage}
            onDeleteMessage={handleDeleteMessage}
          />
        ) : (
          <div className="position-absolute w-100 text-center bottom-50">
            {isLoading ? (
              <p>Loading your chat history with {practName}</p>
            ) : (
              <p>Start a new chat with {practName}</p>
            )}
          </div>
        )}
      </div>

      {!isLoading && (
        <div className="send-msg-form">
          <ChatInput
            onSend={handleSend}
            editMessage={editMessage}
            onCancelEdit={handleCloseEdit}
          />
        </div>
      )}
    </div>
  );
};

export default MessagingInterface;
